import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';

function Calendar() {
  const navigate = useNavigate();
  const [userType, setUserType] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [confirmations, setConfirmations] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [todayLiveJobs, setTodayLiveJobs] = useState([]);
  const [liveConfirmations, setLiveConfirmations] = useState({});
  const [earnings, setEarnings] = useState({ total: 0, confirmed: 0, pending: 0 });
  const user = auth.currentUser;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];

  const openDirections = (address) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'tradepower-live-styles';
    style.textContent = `
      @keyframes livePulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.5); }
        50% { box-shadow: 0 0 0 10px rgba(220, 53, 69, 0); }
      }
      @keyframes dotPulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.4; transform: scale(0.8); }
      }
      .live-dot {
        width: 10px;
        height: 10px;
        background-color: #dc3545;
        border-radius: 50%;
        display: inline-block;
        animation: dotPulse 1.2s ease-in-out infinite;
      }
      .live-card {
        animation: livePulse 2s ease-in-out infinite;
      }
    `;
    if (!document.getElementById('tradepower-live-styles')) {
      document.head.appendChild(style);
    }
    return () => {
      const el = document.getElementById('tradepower-live-styles');
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const type = userDoc.data()?.userType;
        setUserType(type);

        const field = type === 'contractor' ? 'contractorId' : 'workerId';
        const q = query(
          collection(db, 'jobRequests'),
          where(field, '==', user.uid),
          where('status', '==', 'accepted')
        );
        const snapshot = await getDocs(q);
        const jobList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setJobs(jobList);

        const liveToday = jobList.filter(job => {
          if (!job.jobDate || !job.jobEndDate) return false;
          const start = new Date(job.jobDate + 'T00:00:00');
          const end = new Date(job.jobEndDate + 'T00:00:00');
          if (today < start || today > end) return false;
          const dayOfWeek = today.getDay();
          if (dayOfWeek === 6 && !job.includeSaturdays) return false;
          if (dayOfWeek === 0 && !job.includeSundays) return false;
          return true;
        });
        setTodayLiveJobs(liveToday);

        const confMap = {};
        const liveConfMap = {};
        let confirmedEarnings = 0;
        let pendingEarnings = 0;
        let scheduledEarnings = 0;

        for (const job of jobList) {
          const start = new Date(job.jobDate + 'T00:00:00');
          const end = new Date(job.jobEndDate + 'T00:00:00');
          const current = new Date(start);

          while (current <= end) {
            const day = current.getDay();
            const isWeekday = day >= 1 && day <= 5;
            const isSat = day === 6;
            const isSun = day === 0;
            const isWorkDay = isWeekday
              || (isSat && job.includeSaturdays)
              || (isSun && job.includeSundays);

            if (isWorkDay) {
              const dateStr = current.toISOString().split('T')[0];
              const confId = `${job.id}_${dateStr}`;
              const confDoc = await getDoc(doc(db, 'jobConfirmations', confId));
              const rate = type === 'worker'
                ? (job.workerRate || 0)
                : (job.contractorRate || 0);

              if (confDoc.exists()) {
                const confData = confDoc.data();
                confMap[confId] = confData;
                if (dateStr === todayStr) liveConfMap[job.id] = confData;
                const hours = confData.confirmedHours || job.jobDuration || 8;
                if (confData.status === 'verified') {
                  confirmedEarnings += hours * rate;
                } else {
                  pendingEarnings += (job.jobDuration || 8) * rate;
                }
              } else {
                if (dateStr === todayStr) liveConfMap[job.id] = {};
                scheduledEarnings += (job.jobDuration || 8) * rate;
              }
            }
            current.setDate(current.getDate() + 1);
          }
        }

        setConfirmations(confMap);
        setLiveConfirmations(liveConfMap);
        setEarnings({
          confirmed: confirmedEarnings,
          pending: pendingEarnings,
          total: scheduledEarnings,
        });
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const getJobsForDate = (dateStr) => {
    return jobs.filter(job => {
      if (!job.jobDate || !job.jobEndDate) return false;
      const d = new Date(dateStr + 'T00:00:00');
      const start = new Date(job.jobDate + 'T00:00:00');
      const end = new Date(job.jobEndDate + 'T00:00:00');
      if (d < start || d > end) return false;
      const day = d.getDay();
      if (day === 6 && !job.includeSaturdays) return false;
      if (day === 0 && !job.includeSundays) return false;
      return true;
    });
  };

  const getDayStatus = (dateStr) => {
    const dayJobs = getJobsForDate(dateStr);
    if (dayJobs.length === 0) return null;
    const allVerified = dayJobs.every(job => {
      const confId = `${job.id}_${dateStr}`;
      return confirmations[confId]?.status === 'verified';
    });
    if (allVerified) return 'verified';
    const anyConfirmed = dayJobs.some(job => {
      const confId = `${job.id}_${dateStr}`;
      return confirmations[confId]?.workerConfirmed
        || confirmations[confId]?.contractorConfirmed;
    });
    if (anyConfirmed) return 'partial';
    return 'booked';
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} style={styles.emptyDay} />);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const status = getDayStatus(dateStr);
      const isToday = dateStr === todayStr;
      const isSelected = dateStr === selectedDate;

      days.push(
        <div
          key={dateStr}
          style={{
            ...styles.day,
            ...(isToday ? styles.dayToday : {}),
            ...(isSelected ? styles.daySelected : {}),
            ...(status && !isSelected ? styles.dayBooked : {}),
            cursor: status ? 'pointer' : 'default',
          }}
          onClick={() => status && setSelectedDate(isSelected ? null : dateStr)}>
          <span style={{
            ...styles.dayNumber,
            color: isSelected ? 'white' : '#1a1a2e',
          }}>
            {d}
          </span>
          {status && (
            <div style={styles.dayIndicators}>
              {status === 'verified' && <span style={styles.verifiedDot}>✅</span>}
              {status === 'partial' && <span style={styles.partialDot}>⏳</span>}
              {status === 'booked' && <span style={styles.bookedDot}>●</span>}
            </div>
          )}
          {isToday && todayLiveJobs.length > 0 && (
            <span className="live-dot" style={{ marginTop: '2px' }} />
          )}
        </div>
      );
    }
    return days;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${m} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric',
    });
  };

  const getAutoVerifyCountdown = (conf) => {
    if (!conf?.workerConfirmed || conf?.contractorConfirmed) return null;
    const confirmedAt = conf.workerConfirmedAt?.toDate
      ? conf.workerConfirmedAt.toDate()
      : conf.workerConfirmedAt instanceof Date
        ? conf.workerConfirmedAt
        : new Date(conf.workerConfirmedAt);
    if (!confirmedAt || isNaN(confirmedAt)) return null;
    const autoAt = new Date(confirmedAt.getTime() + 24 * 60 * 60 * 1000);
    const msLeft = autoAt - new Date();
    if (msLeft <= 0) return 'Auto-verifying soon...';
    const hLeft = Math.floor(msLeft / (1000 * 60 * 60));
    const mLeft = Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60));
    if (hLeft > 0) return `Auto-confirms in ${hLeft}h ${mLeft}m`;
    return `Auto-confirms in ${mLeft} minutes`;
  };

  if (loading) {
    return <div style={styles.loadingContainer}><p>Loading calendar...</p></div>;
  }

  const monthName = currentMonth.toLocaleDateString('en-US', {
    month: 'long', year: 'numeric',
  });
  const selectedJobs = selectedDate ? getJobsForDate(selectedDate) : [];

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <h1
          style={{ ...styles.logo, cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}>
          TradePower
        </h1>
        <button style={styles.backButton} onClick={() => navigate('/dashboard')}>
          Back
        </button>
      </div>

      <div style={styles.content}>

        {/* LIVE NOW BANNER */}
        {todayLiveJobs.length > 0 && (
          <div style={styles.liveBannerWrapper}>
            <div style={styles.liveTitleRow}>
              <span className="live-dot" />
              <span style={styles.liveTitleText}>LIVE NOW</span>
              <span style={styles.liveDateText}>
                {today.toLocaleDateString('en-US', {
                  weekday: 'long', month: 'short', day: 'numeric',
                })}
              </span>
            </div>

            {todayLiveJobs.map(job => {
              const conf = liveConfirmations[job.id] || {};
              const isWorker = userType === 'worker';
              const rate = isWorker ? (job.workerRate || 0) : (job.contractorRate || 0);
              const bookedHours = job.jobDuration || 8;
              const estimatedPay = Math.round(rate * bookedHours);
              const confirmedPay = conf.confirmedHours
                ? Math.round(rate * conf.confirmedHours)
                : null;
              const autoVerifyMsg = getAutoVerifyCountdown(conf);

              return (
                <div key={job.id} className="live-card" style={styles.liveCard}>

                  {/* Who */}
                  <div style={styles.liveWho}>
                    <span style={styles.liveWhoIcon}>
                      {isWorker ? '🏗️' : '👷'}
                    </span>
                    <div>
                      <p style={styles.liveWhoName}>
                        {isWorker
                          ? (job.contractorEmail || 'Your Contractor')
                          : job.workerName}
                      </p>
                      <p style={styles.liveWhoLabel}>
                        {isWorker ? 'Your contractor today' : 'Worker on site today'}
                      </p>
                    </div>
                    <div style={styles.liveRateBadge}>
                      <span style={styles.liveRate}>${rate}/hr</span>
                    </div>
                  </div>

                  {/* Job Details */}
                  <div style={styles.liveDetails}>

                    <div style={styles.liveDetailRow}>
                      <span style={styles.liveDetailIcon}>📍</span>
                      <div style={styles.liveDetailContent}>
                        <span style={styles.liveDetailText}>{job.jobAddress}</span>
                        <button
                          style={styles.directionsBtn}
                          onClick={() => openDirections(job.jobAddress)}>
                          Get Directions
                        </button>
                      </div>
                    </div>

                    <div style={styles.liveDetailRow}>
                      <span style={styles.liveDetailIcon}>⏰</span>
                      <div style={styles.liveDetailContent}>
                        <span style={styles.liveDetailText}>
                          {formatTime(job.jobTime)} start — {bookedHours} hours booked
                        </span>
                        {job.jobEndDate !== job.jobDate && (
                          <span style={styles.liveDetailSub}>
                            Job runs {job.jobDate} to {job.jobEndDate}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={styles.liveDetailRow}>
                      <span style={styles.liveDetailIcon}>💰</span>
                      <div style={styles.liveDetailContent}>
                        <span style={styles.liveDetailText}>
                          Est. ${estimatedPay} today
                          {confirmedPay && confirmedPay !== estimatedPay && (
                            <span style={styles.confirmedPayText}>
                              {' — $'}{confirmedPay} confirmed
                            </span>
                          )}
                        </span>
                        <span style={styles.liveDetailSub}>{job.jobDescription}</span>
                      </div>
                    </div>

                  </div>

                  {/* Hour Confirmation Status */}
                  <div style={styles.liveConfirmStatus}>
                    <p style={styles.liveConfirmTitle}>Today's Hour Confirmation</p>
                    <div style={styles.liveConfirmRow}>

                      <div style={styles.liveConfirmParty}>
                        <span style={styles.liveConfirmLabel}>Worker</span>
                        {conf.workerConfirmed ? (
                          <span style={styles.liveConfirmedBadge}>
                            {conf.workerHours}h confirmed
                          </span>
                        ) : (
                          <span style={styles.livePendingBadge}>Pending</span>
                        )}
                      </div>

                      <span style={styles.liveConfirmArrow}>—</span>

                      <div style={styles.liveConfirmParty}>
                        <span style={styles.liveConfirmLabel}>Contractor</span>
                        {conf.contractorConfirmed ? (
                          <span style={
                            conf.autoVerified
                              ? styles.liveAutoConfirmedBadge
                              : styles.liveConfirmedBadge
                          }>
                            {conf.contractorHours}h {conf.autoVerified ? '(auto)' : 'confirmed'}
                          </span>
                        ) : (
                          <span style={styles.livePendingBadge}>Pending</span>
                        )}
                      </div>

                      <span style={styles.liveConfirmArrow}>—</span>

                      <div style={styles.liveConfirmParty}>
                        <span style={styles.liveConfirmLabel}>Status</span>
                        {conf.status === 'verified' && (
                          <span style={styles.liveVerifiedBadge}>Verified</span>
                        )}
                        {conf.status === 'disputed' && (
                          <span style={styles.liveDisputedBadge}>Disputed</span>
                        )}
                        {(!conf.status || conf.status === 'pending') && (
                          <span style={styles.livePendingBadge}>Pending</span>
                        )}
                      </div>

                    </div>

                    {autoVerifyMsg && (
                      <p style={styles.liveAutoVerifyText}>{autoVerifyMsg}</p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div style={styles.liveActions}>
                    {((!conf.workerConfirmed && isWorker) ||
                      (!conf.contractorConfirmed && !isWorker)) && (
                      <button
                        style={styles.liveConfirmBtn}
                        onClick={() => navigate('/dashboard')}>
                        Confirm Hours on Dashboard
                      </button>
                    )}
                    <button
                      style={styles.liveMessageBtn}
                      onClick={() => navigate(`/chat/${job.id}`)}>
                      Message {isWorker ? 'Contractor' : 'Worker'}
                    </button>
                  </div>

                </div>
              );
            })}
          </div>
        )}

        {/* EARNINGS SUMMARY */}
        <div style={styles.earningsRow}>
          <div style={styles.earningCard}>
            <span style={styles.earningLabel}>Confirmed</span>
            <span style={styles.earningValue}>
              ${Math.round(earnings.confirmed).toLocaleString()}
            </span>
          </div>
          <div style={styles.earningCard}>
            <span style={styles.earningLabel}>Pending</span>
            <span style={styles.earningValuePending}>
              ${Math.round(earnings.pending).toLocaleString()}
            </span>
          </div>
          <div style={styles.earningCard}>
            <span style={styles.earningLabel}>Scheduled</span>
            <span style={styles.earningValueScheduled}>
              ${Math.round(earnings.total).toLocaleString()}
            </span>
          </div>
        </div>

        {/* CALENDAR */}
        <div style={styles.calendarCard}>
          <div style={styles.calendarHeader}>
            <button
              style={styles.monthNavBtn}
              onClick={() => setCurrentMonth(
                new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
              )}>
              Prev
            </button>
            <h3 style={styles.monthTitle}>{monthName}</h3>
            <button
              style={styles.monthNavBtn}
              onClick={() => setCurrentMonth(
                new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
              )}>
              Next
            </button>
          </div>

          <div style={styles.dayLabels}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} style={styles.dayLabel}>{d}</div>
            ))}
          </div>

          <div style={styles.calendarGrid}>
            {renderCalendar()}
          </div>

          <div style={styles.legend}>
            <span style={styles.legendItem}>Booked</span>
            <span style={styles.legendItem}>Partially confirmed</span>
            <span style={styles.legendItem}>Fully verified</span>
            <span style={{ ...styles.legendItem, color: '#dc3545' }}>Live today</span>
          </div>
        </div>

        {/* SELECTED DAY DETAIL */}
        {selectedDate && selectedJobs.length > 0 && (
          <div style={styles.selectedDayCard}>
            <h3 style={styles.selectedDayTitle}>{formatDate(selectedDate)}</h3>

            {selectedJobs.map(job => {
              const confId = `${job.id}_${selectedDate}`;
              const conf = confirmations[confId] || {};
              const isToday = selectedDate === todayStr;
              const rate = userType === 'worker'
                ? (job.workerRate || 0)
                : (job.contractorRate || 0);
              const hours = conf.confirmedHours || job.jobDuration || 8;
              const dayEarnings = Math.round(rate * hours);

              return (
                <div key={job.id} style={styles.selectedJobCard}>

                  <div style={styles.selectedJobHeader}>
                    <div style={{ flex: 1 }}>
                      <p style={styles.selectedJobTitle}>
                        {userType === 'worker'
                          ? 'Contractor Job'
                          : job.workerName}
                      </p>
                      <p style={styles.selectedJobDesc}>{job.jobDescription}</p>
                    </div>
                    <div style={styles.selectedJobEarnings}>
                      <span style={styles.selectedJobRate}>${dayEarnings}</span>
                      <span style={styles.selectedJobRateLabel}>est. today</span>
                    </div>
                  </div>

                  <div style={styles.selectedJobDetails}>
                    <div style={styles.selectedDetailRow}>
                      <span style={styles.selectedDetailIcon}>📍</span>
                      <span style={styles.selectedDetailText}>{job.jobAddress}</span>
                    </div>
                    <div style={styles.selectedDetailRow}>
                      <span style={styles.selectedDetailIcon}>⏰</span>
                      <span style={styles.selectedDetailText}>
                        {formatTime(job.jobTime)} — {hours} hours
                      </span>
                    </div>
                    <div style={styles.selectedDetailRow}>
                      <span style={styles.selectedDetailIcon}>💰</span>
                      <span style={styles.selectedDetailText}>${rate}/hr</span>
                    </div>
                  </div>

                  <div style={styles.selectedConfirmStatus}>
                    {conf.status === 'verified' && (
                      <div style={styles.verifiedDayBox}>
                        {conf.confirmedHours} hours verified
                        {conf.autoVerified && (
                          <span style={styles.autoTag}> (auto-confirmed)</span>
                        )}
                        <span style={styles.verifiedAmount}>
                          {' — $'}{Math.round(rate * conf.confirmedHours)}
                        </span>
                      </div>
                    )}
                    {conf.status === 'disputed' && (
                      <div style={styles.disputedDayBox}>
                        Hours disputed — Worker: {conf.workerHours}h | Contractor: {conf.contractorHours}h
                      </div>
                    )}
                    {(!conf.status || conf.status === 'pending') && conf.workerConfirmed && (
                      <div style={styles.pendingDayBox}>
                        Worker confirmed {conf.workerHours}h — awaiting contractor
                      </div>
                    )}
                    {(!conf.status || conf.status === 'pending') && !conf.workerConfirmed && isToday && (
                      <div style={styles.pendingDayBox}>
                        Hours not yet confirmed today
                      </div>
                    )}
                  </div>

                  <div style={styles.selectedJobActions}>
                    {isToday && (
                      <button
                        style={styles.selectedActionBtn}
                        onClick={() => navigate('/dashboard')}>
                        Check In on Dashboard
                      </button>
                    )}
                    <button
                      style={styles.selectedActionBtnSecondary}
                      onClick={() => navigate(`/chat/${job.id}`)}>
                      Message
                    </button>
                    <button
                      style={styles.selectedActionBtnSecondary}
                      onClick={() => openDirections(job.jobAddress)}>
                      Directions
                    </button>
                  </div>

                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  loadingContainer: {
    minHeight: '100vh', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  },
  navbar: {
    backgroundColor: 'white', padding: '16px 32px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  logo: { fontSize: '24px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  backButton: {
    padding: '8px 16px', backgroundColor: 'transparent',
    color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer',
  },
  content: {
    maxWidth: '800px', margin: '0 auto', padding: '32px 24px',
    display: 'flex', flexDirection: 'column', gap: '24px',
  },
  liveBannerWrapper: {
    borderRadius: '14px', overflow: 'hidden',
    border: '2px solid #dc3545',
    boxShadow: '0 4px 20px rgba(220,53,69,0.15)',
  },
  liveTitleRow: {
    backgroundColor: '#dc3545', padding: '12px 20px',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  liveTitleText: {
    fontSize: '14px', fontWeight: 'bold',
    color: 'white', letterSpacing: '2px',
  },
  liveDateText: {
    fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginLeft: 'auto',
  },
  liveCard: {
    backgroundColor: 'white', padding: '20px',
    display: 'flex', flexDirection: 'column', gap: '16px',
  },
  liveWho: { display: 'flex', alignItems: 'center', gap: '12px' },
  liveWhoIcon: { fontSize: '32px', flexShrink: 0 },
  liveWhoName: {
    fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '2px',
  },
  liveWhoLabel: { fontSize: '12px', color: '#999' },
  liveRateBadge: {
    marginLeft: 'auto', backgroundColor: '#fff0f3',
    borderRadius: '8px', padding: '8px 12px', flexShrink: 0,
  },
  liveRate: { fontSize: '18px', fontWeight: 'bold', color: '#E94560' },
  liveDetails: {
    backgroundColor: '#f8f9fa', borderRadius: '10px',
    padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px',
  },
  liveDetailRow: { display: 'flex', alignItems: 'flex-start', gap: '10px' },
  liveDetailIcon: { fontSize: '16px', flexShrink: 0, marginTop: '1px' },
  liveDetailContent: { display: 'flex', flexDirection: 'column', gap: '4px' },
  liveDetailText: { fontSize: '14px', color: '#1a1a2e', fontWeight: '500' },
  liveDetailSub: { fontSize: '12px', color: '#999' },
  directionsBtn: {
    backgroundColor: 'transparent', border: 'none',
    color: '#E94560', fontSize: '12px', fontWeight: 'bold',
    cursor: 'pointer', padding: 0, textAlign: 'left',
  },
  confirmedPayText: { color: '#28a745', fontWeight: 'bold' },
  liveConfirmStatus: {
    backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '14px 16px',
  },
  liveConfirmTitle: {
    fontSize: '12px', fontWeight: 'bold', color: '#555',
    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px',
  },
  liveConfirmRow: {
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
  },
  liveConfirmParty: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: '4px', flex: 1,
  },
  liveConfirmLabel: { fontSize: '11px', color: '#666', fontWeight: 'bold' },
  liveConfirmedBadge: {
    backgroundColor: '#d4edda', color: '#155724',
    padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold',
  },
  liveAutoConfirmedBadge: {
    backgroundColor: '#e9ecef', color: '#6c757d',
    padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold',
  },
  livePendingBadge: {
    backgroundColor: '#f0f0f0', color: '#999',
    padding: '4px 10px', borderRadius: '8px', fontSize: '12px',
  },
  liveVerifiedBadge: {
    backgroundColor: '#d4edda', color: '#155724',
    padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold',
  },
  liveDisputedBadge: {
    backgroundColor: '#fff3cd', color: '#856404',
    padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold',
  },
  liveConfirmArrow: { color: '#ddd', fontSize: '18px', flexShrink: 0 },
  liveAutoVerifyText: {
    fontSize: '12px', color: '#276749',
    marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0',
  },
  liveActions: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  liveConfirmBtn: {
    flex: 1, padding: '12px', backgroundColor: '#28a745', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontSize: '13px', fontWeight: 'bold', minWidth: '160px',
  },
  liveMessageBtn: {
    flex: 1, padding: '12px', backgroundColor: 'transparent', color: '#E94560',
    border: '1px solid #E94560', borderRadius: '8px', cursor: 'pointer',
    fontSize: '13px', fontWeight: 'bold', minWidth: '140px',
  },
  earningsRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' },
  earningCard: {
    backgroundColor: 'white', borderRadius: '10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '16px',
    display: 'flex', flexDirection: 'column', gap: '6px', textAlign: 'center',
  },
  earningLabel: { fontSize: '12px', color: '#666', fontWeight: 'bold' },
  earningValue: { fontSize: '22px', fontWeight: 'bold', color: '#28a745' },
  earningValuePending: { fontSize: '22px', fontWeight: 'bold', color: '#856404' },
  earningValueScheduled: { fontSize: '22px', fontWeight: 'bold', color: '#2c5282' },
  calendarCard: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '24px',
  },
  calendarHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '20px',
  },
  monthNavBtn: {
    padding: '8px 20px', backgroundColor: 'transparent',
    border: '1px solid #ddd', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', color: '#555',
  },
  monthTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e' },
  dayLabels: {
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '8px',
  },
  dayLabel: {
    textAlign: 'center', fontSize: '12px',
    fontWeight: 'bold', color: '#999', padding: '4px',
  },
  calendarGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px',
  },
  emptyDay: { height: '56px' },
  day: {
    height: '56px', borderRadius: '8px',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    gap: '2px', transition: 'background-color 0.15s',
  },
  dayToday: { backgroundColor: '#fff0f3', border: '2px solid #E94560' },
  daySelected: { backgroundColor: '#E94560' },
  dayBooked: { backgroundColor: '#f0f8ff' },
  dayNumber: { fontSize: '14px' },
  dayIndicators: { display: 'flex', gap: '2px' },
  verifiedDot: { fontSize: '10px' },
  partialDot: { fontSize: '10px' },
  bookedDot: { fontSize: '8px', color: '#E94560' },
  legend: {
    display: 'flex', gap: '16px', flexWrap: 'wrap',
    marginTop: '16px', paddingTop: '16px',
    borderTop: '1px solid #f0f0f0',
    fontSize: '12px', color: '#666',
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: '4px' },
  selectedDayCard: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '24px',
  },
  selectedDayTitle: {
    fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '16px',
  },
  selectedJobCard: {
    backgroundColor: '#f8f9fa', borderRadius: '10px',
    padding: '16px', marginBottom: '12px',
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
  selectedJobHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', gap: '12px',
  },
  selectedJobTitle: {
    fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px',
  },
  selectedJobDesc: { fontSize: '13px', color: '#666' },
  selectedJobEarnings: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0,
  },
  selectedJobRate: { fontSize: '20px', fontWeight: 'bold', color: '#28a745' },
  selectedJobRateLabel: { fontSize: '11px', color: '#999' },
  selectedJobDetails: { display: 'flex', flexDirection: 'column', gap: '6px' },
  selectedDetailRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  selectedDetailIcon: { fontSize: '14px', flexShrink: 0 },
  selectedDetailText: { fontSize: '13px', color: '#555' },
  selectedConfirmStatus: { display: 'flex', flexDirection: 'column', gap: '6px' },
  verifiedDayBox: {
    backgroundColor: '#d4edda', color: '#155724',
    padding: '8px 12px', borderRadius: '8px',
    fontSize: '13px', fontWeight: 'bold',
  },
  autoTag: { fontWeight: 'normal', fontSize: '11px', color: '#6c757d' },
  verifiedAmount: { color: '#155724' },
  disputedDayBox: {
    backgroundColor: '#fff3cd', color: '#856404',
    padding: '8px 12px', borderRadius: '8px',
    fontSize: '13px', fontWeight: 'bold',
  },
  pendingDayBox: {
    backgroundColor: '#f0f0f0', color: '#666',
    padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
  },
  selectedJobActions: {
    display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center',
  },
  selectedActionBtn: {
    padding: '10px 16px', backgroundColor: '#E94560', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontSize: '13px', fontWeight: 'bold',
  },
  selectedActionBtnSecondary: {
    padding: '10px 16px', backgroundColor: 'transparent', color: '#555',
    border: '1px solid #ddd', borderRadius: '8px',
    cursor: 'pointer', fontSize: '13px',
  },
};

export default Calendar;